import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable } from 'rxjs'
import * as UUID from 'uuid'
import { Message } from './message'
import { MessageScope } from './message-scope'
import { MessageType } from './message-type'

/**
 * Injectable service that provides a storage location for messages
 * to be displayed in the application.
 */
@Injectable({
  providedIn: 'root'
})
export class MessageService {

  private readonly _messages$: BehaviorSubject<Message[]> = new BehaviorSubject([])

  /**
   * Add a message to the collection of active messages
   *
   * @param value The message being added
   */
  add(value: Message): void {
    // Check if the incoming message already exists in the collection
    let messages = this._messages$.value
    let containsMessage = messages.some(it => it.text === value.text && it.level === value.level)

    if (!containsMessage) {
      value.id = UUID.v4()
      messages.push(value)
      this._messages$.next(messages)
    }
  }

  /**
   * Remove a message from the collection of active messages
   *
   * @param value The message to be removed
   */
  remove(value: Message) {
    let filtered = this._messages$
      .value
      .filter(it => it.id !== value.id)

    this._messages$.next(filtered)
  }

  /**
   * Clear all messages
   *
   * @param args  A collection of arguments that will be used to determine what type
   *              and scope of messages will be cleared
   */
  clear(args: { type?: MessageType; scope?: MessageScope } = { type: null, scope: MessageScope.SESSION }): void {
    let clearedMessages = this._messages$.value
      .filter(it => {
        let typePassed = args.type != null ?
          it.type !== args.type :
          false

        let scopePassed = it.scope !== null
          && it.scope > args.scope

        return scopePassed
            || typePassed
      })

    this._messages$.next(clearedMessages)
  }

  /**
   * Observable collection of messages
   */
  get messages$(): Observable<Message[]> {
    return this._messages$.asObservable()
  }

}
